import { useState, useRef, useEffect } from 'react';
import { UploadCloud, FileAudio, CheckCircle2 } from 'lucide-react';
import { uploadFile } from '../lib/api';
import { ProgressBar } from './ProgressBar';
import { LoadingSpinner } from './LoadingSpinner';
import './AudioUploader.css';

interface AudioUploaderProps {
  onUploadComplete: (audioFileId: string, fileUrl?: string) => void;
  onError: (error: string) => void;
}

export default function AudioUploader({ onUploadComplete, onError }: AudioUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleTriggerUpload = () => {
      fileInputRef.current?.click();
    };
    window.addEventListener('trigger-upload', handleTriggerUpload);
    return () => {
      window.removeEventListener('trigger-upload', handleTriggerUpload);
    };
  }, []);

  const handleFile = async (file: File) => {
    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/flac', 'audio/mp4', 'audio/aac'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|flac|m4a|aac)$/i)) {
      onError('Invalid file format. Please upload MP3, WAV, FLAC, M4A, or AAC files.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      onError('File size exceeds 50MB limit.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setIsReady(false);
    setUploadedFile(null);

    try {
      setUploadedFile(file);
      const result = await uploadFile(file, 8, (progress) => {
        setUploadProgress(progress);
      });
      setUploadProgress(100);
      setIsUploading(false);
      setIsReady(true);
      const playbackUrl = result.fileUrl || URL.createObjectURL(file);
      setFileUrl(playbackUrl);
      onUploadComplete(result.audioFileId, playbackUrl);
    } catch (error: any) {
      setIsUploading(false);
      setIsReady(false);
      setUploadedFile(null);
      onError(error.response?.data?.error || error.message || 'Upload failed');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="audio-uploader">
      <div
        className={`upload-zone ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''} ${isReady ? 'ready' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !isReady && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />

        {isUploading ? (
          <div className="upload-status">
            <LoadingSpinner size="md" color="cyan" />
            <p className="upload-status-text">Uploading…</p>
            <ProgressBar value={uploadProgress} label="Uploading" color="cyan" />
          </div>
        ) : isReady && fileUrl ? (
          <div className="upload-ready" onClick={(e) => e.stopPropagation()}>
            <div className="ready-icon-wrap">
              <CheckCircle2 size={32} strokeWidth={1.6} />
            </div>
            <h3>Ready for analysis</h3>
            <p className="ready-text">File uploaded successfully</p>
            <p className="uploaded-filename">
              <FileAudio size={14} strokeWidth={1.8} />
              {uploadedFile?.name || 'Audio file'}
            </p>
          </div>
        ) : (
          <div className="upload-content">
            <div className="upload-icon-wrap">
              <UploadCloud size={28} strokeWidth={1.5} />
            </div>
            <p className="upload-eyebrow">Upload</p>
            <h3>Drop an audio file here</h3>
            <p className="upload-sub">or click to browse</p>
            <p className="upload-hint">MP3 · WAV · FLAC · M4A · AAC — max 50 MB</p>
          </div>
        )}
      </div>
    </div>
  );
}
